export interface AnalyticsPopupConfig {
  title: string;
  description: string;
  acceptButtonText: string;
  acceptButtonBgColor: string;
  acceptButtonTextColor: string;
  rejectButtonText: string;
  rejectButtonBgColor: string;
  rejectButtonTextColor: string;
  preferencesButtonText: string;
  cookiePolicyUrl: string;
  cookiePolicyLinkText: string;
  privacyPolicyUrl: string;
  privacyPolicyLinkText: string;
  additionalInfoUrl: string;
  additionalInfoLinkText: string;
  essentialTitle: string;
  essentialDescription: string;
  analyticsTitle: string;
  analyticsDescription: string;
  savePreferencesButtonText: string;
  cookieSettingsLabel: string;
  policyVersion: string;
  showTitle: boolean;
  showAcceptButton: boolean;
  showRejectButton: boolean;
  showPreferencesButton: boolean;
  showCloseButton: boolean;
}

const DEFAULT_DESCRIPTION = [
  "We use essential cookies to securely verify your identity and provide access",
  "to your matchday photos.",
  "",
  "With your permission, we also use Google Analytics to understand how",
  "supporters use the platform, including website visits, photo views,",
  "downloads, and other interactions. This helps us measure engagement and",
  "improve the experience.",
  "",
  "You can accept or reject analytics tracking at any time. Your choice will",
  "not affect your ability to access your photos.",
  "",
  "For more information, please review our Cookie Policy and Privacy Notice.",
].join("\n");

export function defaultAnalyticsPopupConfig(): AnalyticsPopupConfig {
  return {
    title: "Your Privacy Matters",
    description: DEFAULT_DESCRIPTION,
    acceptButtonText: "Accept Analytics",
    acceptButtonBgColor: "#111111",
    acceptButtonTextColor: "#ffffff",
    rejectButtonText: "Reject Analytics",
    rejectButtonBgColor: "#e6e6e6",
    rejectButtonTextColor: "#111111",
    preferencesButtonText: "Manage Cookie Preferences",
    cookiePolicyUrl: "",
    cookiePolicyLinkText: "Cookie Policy",
    privacyPolicyUrl: "",
    privacyPolicyLinkText: "Privacy Notice",
    additionalInfoUrl: "",
    additionalInfoLinkText: "Learn More",
    essentialTitle: "Essential Cookies",
    essentialDescription: [
      "These cookies and similar technologies are necessary for secure",
      "authentication, FanID verification, and the operation of the platform.",
      "They cannot be disabled through these settings.",
    ].join(" "),
    analyticsTitle: "Analytics Cookies",
    analyticsDescription: [
      "With your permission, we use Google Analytics to understand how",
      "supporters interact with the platform, including website visits, photo",
      "views, downloads, and other engagement activity. This information helps",
      "us measure performance and improve the experience.",
    ].join(" "),
    savePreferencesButtonText: "Save Preferences",
    cookieSettingsLabel: "Cookie Settings",
    policyVersion: "1",
    showTitle: true,
    showAcceptButton: true,
    showRejectButton: true,
    showPreferencesButton: true,
    showCloseButton: true,
  };
}

function flag(value: unknown, fallback: boolean): boolean {
  if (value === true || value === "yes") return true;
  if (value === false || value === "no") return false;
  return fallback;
}

function clip(value: unknown, max: number, fallback: string): string {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return fallback;
  return raw.slice(0, max);
}

function hexColor(value: unknown, fallback: string): string {
  const raw = typeof value === "string" ? value.trim() : "";
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(raw)) return raw.toLowerCase();
  return fallback;
}

function httpsUrl(value: unknown): string {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== "https:") return "";
    return parsed.toString().slice(0, 500);
  } catch {
    return "";
  }
}

function cleanDescription(value: unknown, fallback: string): string {
  const raw = typeof value === "string" ? value : "";
  const stripped = raw.replace(/<[^>]*>/g, "").split(String.fromCharCode(0)).join("");
  const trimmed = stripped.trim();
  if (!trimmed) return fallback;
  return trimmed.slice(0, 4000);
}

export function sanitizeAnalyticsPopupConfig(input: unknown): AnalyticsPopupConfig {
  const defaults = defaultAnalyticsPopupConfig();
  const src = input && typeof input === "object"
    ? input as Partial<AnalyticsPopupConfig>
    : {};
  return {
    title: clip(src.title, 120, defaults.title),
    description: cleanDescription(src.description, defaults.description),
    acceptButtonText: clip(src.acceptButtonText, 40, defaults.acceptButtonText),
    acceptButtonBgColor: hexColor(src.acceptButtonBgColor, defaults.acceptButtonBgColor),
    acceptButtonTextColor: hexColor(src.acceptButtonTextColor, defaults.acceptButtonTextColor),
    rejectButtonText: clip(src.rejectButtonText, 40, defaults.rejectButtonText),
    rejectButtonBgColor: hexColor(src.rejectButtonBgColor, defaults.rejectButtonBgColor),
    rejectButtonTextColor: hexColor(src.rejectButtonTextColor, defaults.rejectButtonTextColor),
    preferencesButtonText: clip(src.preferencesButtonText, 60, defaults.preferencesButtonText),
    cookiePolicyUrl: httpsUrl(src.cookiePolicyUrl),
    cookiePolicyLinkText: clip(src.cookiePolicyLinkText, 80, defaults.cookiePolicyLinkText),
    privacyPolicyUrl: httpsUrl(src.privacyPolicyUrl),
    privacyPolicyLinkText: clip(src.privacyPolicyLinkText, 80, defaults.privacyPolicyLinkText),
    additionalInfoUrl: httpsUrl(src.additionalInfoUrl),
    additionalInfoLinkText: clip(
      src.additionalInfoLinkText,
      80,
      defaults.additionalInfoLinkText,
    ),
    essentialTitle: clip(src.essentialTitle, 80, defaults.essentialTitle),
    essentialDescription: clip(src.essentialDescription, 1000, defaults.essentialDescription),
    analyticsTitle: clip(src.analyticsTitle, 80, defaults.analyticsTitle),
    analyticsDescription: clip(src.analyticsDescription, 1000, defaults.analyticsDescription),
    savePreferencesButtonText: clip(
      src.savePreferencesButtonText,
      40,
      defaults.savePreferencesButtonText,
    ),
    cookieSettingsLabel: clip(src.cookieSettingsLabel, 40, defaults.cookieSettingsLabel),
    policyVersion: clip(src.policyVersion, 20, defaults.policyVersion).replace(/[^\w.-]/g, "")
      || defaults.policyVersion,
    showTitle: flag(src.showTitle, defaults.showTitle),
    showAcceptButton: flag(src.showAcceptButton, defaults.showAcceptButton),
    showRejectButton: flag(src.showRejectButton, defaults.showRejectButton),
    showPreferencesButton: flag(src.showPreferencesButton, defaults.showPreferencesButton),
    showCloseButton: flag(src.showCloseButton, defaults.showCloseButton),
  };
}

export type AnalyticsDescriptionPart =
  | { type: "text"; text: string }
  | { type: "bold"; text: string }
  | { type: "italic"; text: string }
  | { type: "link"; text: string; href: string };

const INLINE_RE = /(\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\((https:\/\/[^)\s]+)\))/g;

export function parseAnalyticsDescription(source: string): AnalyticsDescriptionPart[][] {
  const text = String(source || "").replace(/\r\n/g, "\n");
  return text.split(/\n{2,}/).map((paragraph) => {
    const parts: AnalyticsDescriptionPart[] = [];
    const lines = paragraph.split("\n");
    lines.forEach((line, lineIndex) => {
      if (lineIndex > 0) parts.push({ type: "text", text: "\n" });
      let last = 0;
      INLINE_RE.lastIndex = 0;
      let match = INLINE_RE.exec(line);
      while (match) {
        if (match.index > last) {
          parts.push({ type: "text", text: line.slice(last, match.index) });
        }
        if (match[2]) parts.push({ type: "bold", text: match[2] });
        else if (match[3]) parts.push({ type: "italic", text: match[3] });
        else if (match[4] && match[5]) {
          parts.push({ type: "link", text: match[4], href: match[5] });
        }
        last = match.index + match[0].length;
        match = INLINE_RE.exec(line);
      }
      if (last < line.length) parts.push({ type: "text", text: line.slice(last) });
    });
    return parts.filter((part) => part.type !== "text" || part.text.length > 0);
  }).filter((paragraph) => paragraph.length > 0);
}
